import { Injectable, QueryList } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngxs/store'
import { FormComponent } from 'src/app/shared/components/form/form.component'
import { LoaderModalComponent } from 'src/app/shared/components/loader-modal/loader-modal.component'
import { LocalStorageService } from 'src/app/shared/services/local-storage.service'
import { OrderService } from 'src/app/shared/services/order.service'
import { OrderStatusEnum } from 'src/enums/orderStatus.enum'
import { RolesType } from 'src/enums/roles.enum'
import { IAddress } from 'src/interfaces/auth/auth.interface'
import { ResetCartStateAction } from 'src/store/cart/cart.meta.actions'
import { CartMetaState } from 'src/store/cart/cart.meta.state'
import {
  SetChargerKitCompositionAction,
  SetCheckoutKitTypeAction,
  SetCommissionValuesAction,
  SetDeliveryToAction,
  SetDiscountAction,
  SetDiscountCouponAction,
  SetExternalUserAction,
  SetInstallationValuesAction,
  SetIntegratorAction,
  SetInvoiceToAction,
  SetOrderInstallationStatusAction,
  SetPaymentMethodAction,
  SetPaymentOriginAction,
  SetProposalDataAction,
  SetQuoteDataItemAction,
  SetRebateValueAction,
  SetShippingAction,
  SetStandardKitCompositionAction,
  SetTabFilterAction,
  SetTotvsCodeAction,
  SetUploadedFilesAction,
  UpdateQuoteItemAction,
} from 'src/store/quote/quote.meta.actions'
import {
  IFilters,
  IQuoteItem,
  KitType,
  StoreChargers,
  TabFilters,
} from 'src/store/quote/quote.meta.model'
import { QuoteMetaState } from 'src/store/quote/quote.meta.state'
import { SetShowControlAction } from 'src/store/user/user.meta.actions'
import { UserMetaState } from 'src/store/user/user.meta.state'
import { FreightType, ParseQuotationType } from './../enums/quotation.enum'
import { Utils } from './utils.helper'
import {
  BaseOrderKitContent,
  OrderKitContent,
  OrderStandardKitContent,
  Product,
} from 'src/interfaces/order/order.interface'
import { ICurrentUser } from 'src/store/user/user.meta.model'
import { StoreKit } from 'src/interfaces/kit/kit.interface'

export enum CheckOrderActions {
  MISSING_DATA = 'MISSING_DATA',
}

export enum CheckOrderActionTypes {
  INVOICE_TO = 'INVOICE_TO',
  DELIVERY_TO = 'DELIVERY_TO',
}

export enum OrderTypes {
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  PROPOSAL = 'PROPOSAL',
}

interface IInvoiceToDeliverTo {
  document: string
  documentType: string
  email: string
  fullName: string
  phone: string
  stateRegistration: string
  leadId?: string
  userId?: string
  address?: Partial<IAddress>
}

interface IProposalPayload {
  title: string
  kitContent: any
  invoiceTo: IInvoiceToDeliverTo
  validity: Date
  rebate: number
  shipping: {
    freightType: string
    shippingCost: number
  }
  status: OrderStatusEnum
  type: OrderTypes
  deliverTo: IInvoiceToDeliverTo
  couponId: string
  installationValues: {
    percentage: number
    price: number
  }
  commissionValues: {
    percentage: number
    price: number
  }
  tabFilters: IFilters
  externalUserId: string
}

@Injectable({
  providedIn: 'root',
})
export class OrderHelper {
  typesMatrix = {
    PANEL: 'panels',
    INVERTER: 'inverters',
    BRACKET: 'brackets',
    Protection: 'protections',
    Connectors: 'connectors',
    CABLE: 'cables',
    ACCESSORY: 'accessories',
  }
  integratorEditAllowedRoles = [
    RolesType.ADMIN,
    RolesType.BACKOFFICE,
    RolesType.BUSINESS_1,
    RolesType.BUSINESS_2,
  ]

  formComponent: QueryList<FormComponent>
  constructor(
    private store: Store,
    private utils: Utils,
    private dialog: MatDialog,
    private orderService: OrderService,
    public storage: LocalStorageService,
  ) {}

  saveOrder({ skipPreSave }: { skipPreSave?: boolean }) {
    const loader = this.dialog.open(LoaderModalComponent, {
      width: '500px',
      height: '300px',
      panelClass: 'main-modal',
      disableClose: true,
      data: 'Aguarde enquanto carregando algumas informações...',
    })
    const data = this.parseQuotationData({
      parseQuotationType: ParseQuotationType.CREATE,
      skipPreSave,
    })
    this.orderService.createQuotation(data).subscribe({
      next: (response: any) => {
        this.store.dispatch(new SetProposalDataAction(response))
        this.store.dispatch(new SetShippingAction(response.shipping))
        response.invoiceTo.displayName = response.invoiceTo.fullName
        this.store.dispatch(new SetInvoiceToAction(response.invoiceTo))
        const deliveryTo = {
          ...response.deliverTo,
          address: response.deliverTo.address,
          displayName: response.deliverTo.fullName,
        }
        this.store.dispatch(new SetDeliveryToAction(deliveryTo))
        this.resetCart()
        loader.close()
        this.store.dispatch(new SetShowControlAction(true))
        this.clearProposalDrafts()
        if (!skipPreSave) {
          this.goToPreCheckout(response.id)
          return
        }
        this.utils
          .showSuccessMessage('Sucesso', 'Proposta salva com sucesso.')
          .then(() => {
            this.utils.navigateTo('/proposal/checkout/' + response.orderNumber)
          })
      },
      error: (error) => {
        loader.close()
        const errorMessage = this.utils.getErrorMessage(error)

        if (errorMessage === 'Shipping cost invalid to fright type CIF') {
          this.refreshShippingCost(data.shipping.freightType as FreightType)
          return
        }

        if (errorMessage === 'Installation price invalid') {
          this.refreshInstallationPrice(data.installationValues.percentage)
          return
        }

        this.utils.showErrorMessage(
          'Erro',
          errorMessage || 'Ocorreu um erro ao criar a ordem.',
        )
      },
    })
  }

  editProductQuantity() {
    const loader = this.dialog.open(LoaderModalComponent, {
      width: '500px',
      height: '300px',
      panelClass: 'main-modal',
      disableClose: true,
      data: 'Aguarde enquanto carregando algumas informações...',
    })
    const data = this.parseQuotationData({
      parseQuotationType: ParseQuotationType.EDIT,
    })
    const orderId = this.store.selectSnapshot(QuoteMetaState.getProposalData).id
    this.orderService.edit(orderId, data).subscribe({
      next: (response: any) => {
        this.store.dispatch(new SetProposalDataAction(response))
        loader.close()
      },
      error: (error) => {
        console.log(error)
        loader.close()
        const errorMessage = this.utils.getErrorMessage(error)
        this.utils
          .showErrorMessage(
            'Erro',
            errorMessage || 'Ocorreu um erro ao editar a ordem.',
          )
          .catch((err) => {
            console.log(err)
          })
      },
    })
  }

  parseQuotationData({
    parseQuotationType,
    skipPreSave,
  }: {
    parseQuotationType: ParseQuotationType
    skipPreSave?: boolean
  }) {
    const externalUser = this.store.selectSnapshot(
      QuoteMetaState.getExternalUser,
    )
    const user = this.store.selectSnapshot(UserMetaState.getUser)
    const isDealer = user.roles[0] === RolesType.DEALER
    const externalUserId =
      parseQuotationType === ParseQuotationType.CREATE && isDealer
        ? user.id
        : externalUser?.id || null
    const validity = new Date()
    validity.setDate(validity.getDate() + 14)
    const kitType = this.utils.getCheckoutKitType()
    const status = OrderStatusEnum.CREATED
    const type = OrderTypes.PROPOSAL
    const commissionValues = this.store.selectSnapshot(
      QuoteMetaState.getCommissionValues,
    )
    const discountCoupon = this.store.selectSnapshot(
      QuoteMetaState.getDiscountCoupon,
    )
    const title = this.getTitle(kitType)
    const tabFilters = this.store.selectSnapshot(QuoteMetaState.getTabFilters)

    const isCurrentUserDealer = user.roles.includes(RolesType.DEALER)

    if (!skipPreSave && isCurrentUserDealer && kitType === KitType.CHARGER)
      this.store.dispatch(new SetInstallationValuesAction(null))

    const kitContent = this.getKitContent()
    const deliverTo = this.getDeliverTo({
      useStore: parseQuotationType === ParseQuotationType.EDIT || skipPreSave,
      user,
    })
    const invoiceTo = this.getInvoiceTo({
      useStore: parseQuotationType === ParseQuotationType.EDIT || skipPreSave,
      user,
    })
    const shipping = this.getShipping()
    const installationValues = this.getInstallationValues({
      generateInstallationValue:
        parseQuotationType === ParseQuotationType.EDIT || skipPreSave,
      kitType,
    })

    const proposal: IProposalPayload = {
      title,
      kitContent,
      invoiceTo,
      validity,
      rebate: 0,
      shipping,
      status,
      type,
      deliverTo,
      couponId: discountCoupon?.id,
      installationValues,
      commissionValues,
      tabFilters,
      externalUserId,
    }

    if (
      parseQuotationType === ParseQuotationType.CREATE &&
      user.roles.includes(RolesType.DEALER)
    ) {
      delete proposal.commissionValues
    }

    return proposal
  }

  getProducts(selectedItems: any[]): any[] {
    const kitTypes = ['STANDARD', 'FINAME']
    return selectedItems.map((item) => {
      if (!kitTypes.includes(item.type)) {
        const product = {
          ...item,
          productId: item.id,
          productType: item.type,
          quantity: Number(item.quantity),
          value: isNaN(item.value)
            ? Number(item.value.replace(/\D/g, '')) / 100
            : item.value,
        }
        return product
      }
    })
  }

  getProductIds(products: any[]): string[] {
    return products.map((product) => product.id)
  }

  calculateCustomKitTotalWeight(products: any[]) {
    const parsedProducts = products.map((product) => {
      const parsedWeight = product.weight ? product.weight.replace(',', '.') : 0
      return {
        ...product,
        weight: parseFloat(parsedWeight),
      }
    })
    const totalProductWeight = parsedProducts.reduce((acc, product) => {
      return acc + product.quantity * product.weight
    }, 0)
    const painels = parsedProducts.filter((product) => product.type === 'PANEL')
    const panelQuantity = painels[0].quantity
    const panelsInPalet = painels[0].origin === 'IMPORTED' ? 31 : 30
    return totalProductWeight + Math.ceil(panelQuantity / panelsInPalet) * 50
  }

  getProductAmounts(products: any[]): any[] {
    return products.map((product) => {
      return {
        productId: product.id,
        quantity: Number(product.quantity),
        productType: product.type,
        price: product.price,
      }
    })
  }

  getKitContent(): Partial<OrderKitContent[]> {
    const kitType = this.utils.getCheckoutKitType()
    const kitContentMatrix = {
      [KitType.CHARGER]: this.getChargerKitContent(),
      [KitType.STANDARD]: this.getStandardKitContent(),
      [KitType.CUSTOM]: this.getCustomKitContent(),
    }
    return kitContentMatrix[kitType]
  }

  getStandardKitContent(): OrderStandardKitContent[] {
    const selectedItems = this.store.selectSnapshot(
      QuoteMetaState.getStandardKitComposition,
    )
    const equipmentPrice = this.store.selectSnapshot(
      QuoteMetaState.getEquipmentPrice,
    )
    return selectedItems.map((product) => {
      return {
        kitType: KitType.STANDARD,
        productIds: product.productIds,
        productAmounts: product.productAmounts,
        costPerWatt: product.costPerWatt,
        totalOverload: product.totalOverload,
        totalPotency: product.totalPotency,
        quantity: Number(product.quantity),
        totalWeight: product.totalWeight,
        totalPrice: product.quantity * product.value,
        totvsCode: product.totvsCode,
        selectedItemsTypeModel: product?.selectedItemsTypeModel || null,
        kitId: product.id,
        kitName: product.title,
        equipmentPrice,
      }
    })
  }

  getCustomKitContent(): BaseOrderKitContent[] {
    const selectedItems = this.store.selectSnapshot(
      QuoteMetaState.getSelectedItems,
    )
    const productIds = this.getProductIds(selectedItems)
    const products = this.getProducts(selectedItems)
    const costPerWatt = this.store.selectSnapshot(QuoteMetaState.getCostPerWatt)
    const overload = this.store.selectSnapshot(QuoteMetaState.getOverload)
    const selectedItemsTypeModel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedItemsTypeModel,
    )
    const totalWeight = products[0]
      ? this.calculateCustomKitTotalWeight(products)
      : 0
    const panelPotency = products[0]
      ? products.find((product) => product.type === 'PANEL')?.potency
      : 0
    const totvsCode = products[0]
      ? this.utils.generateTotvs(products, panelPotency)
      : ''
    const totalPotency = this.store.selectSnapshot(
      QuoteMetaState.getTotalPotency,
    )
    const totalPrice = this.store.selectSnapshot(
      QuoteMetaState.getProposalTotalPrice,
    )
    return [
      {
        kitType: KitType.CUSTOM,
        productIds,
        productAmounts: this.getProductAmounts(products),
        costPerWatt: parseFloat(costPerWatt?.toFixed(2)),
        totalOverload: Number(overload),
        totalPotency,
        quantity: 1,
        totalWeight: parseFloat(totalWeight?.toFixed(2)),
        totalPrice: parseFloat(totalPrice?.toFixed(2)),
        totvsCode,
        selectedItemsTypeModel,
      },
    ]
  }

  getChargerKitContent(): BaseOrderKitContent[] {
    const kitComposition = this.store.selectSnapshot(
      QuoteMetaState.getChargerKitComposition,
    )
    const productIds = this.getProductIds(kitComposition)
    const products = this.getProducts(kitComposition)
    const costPerWatt = this.store.selectSnapshot(QuoteMetaState.getCostPerWatt)
    const totalWeight = products.reduce((acc, product) => {
      return acc + product.quantity * (product.weight || 0)
    }, 0)
    const totvsCode = ''
    const totalPotency = this.store.selectSnapshot(
      QuoteMetaState.getTotalPotency,
    )
    const totalPrice = this.store.selectSnapshot(
      QuoteMetaState.getProposalTotalPrice,
    )
    return [
      {
        kitType: KitType.CHARGER,
        productIds,
        productAmounts: this.getProductAmounts(products),
        costPerWatt: parseFloat(costPerWatt?.toFixed(2)),
        totalOverload: 0,
        totalPotency,
        quantity: 1,
        totalWeight: parseFloat(totalWeight?.toFixed(2)),
        totalPrice: parseFloat(totalPrice?.toFixed(2)),
        totvsCode,
        selectedItemsTypeModel: 'EV Charger',
      },
    ]
  }

  getDeliverTo({ useStore, user }: { useStore: boolean; user: ICurrentUser }) {
    const deliveryTo = useStore
      ? this.store.selectSnapshot(QuoteMetaState.getDeliveryTo)
      : user

    return {
      fullName: deliveryTo.firstName?.trim() || deliveryTo.displayName?.trim(),
      document: deliveryTo.document,
      documentType: deliveryTo.documentType ? deliveryTo.documentType : 'CPF',
      address: {
        zipCode: deliveryTo.address?.zipCode,
        street: deliveryTo.address?.street,
        number: deliveryTo.address?.number?.toString(),
        complement: deliveryTo.address?.complement,
        neighborhood: deliveryTo.address?.neighborhood,
        city: deliveryTo.address?.city,
        state: deliveryTo.address?.state,
        coordinates: deliveryTo.address?.coordinates,
      },
      stateRegistration: deliveryTo?.stateRegistration,
      email: deliveryTo.email,
      phone: deliveryTo.phone,
    }
  }

  getInvoiceTo({ useStore, user }: { useStore: boolean; user: ICurrentUser }) {
    const invoiceTo = useStore
      ? this.store.selectSnapshot(QuoteMetaState.getInvoiceTo)
      : user
    const sourceInvoiceToId = invoiceTo.id === user.id ? 'userId' : 'leadId'

    return {
      fullName: invoiceTo.firstName?.trim() || invoiceTo.displayName?.trim(),
      document: invoiceTo.document,
      documentType: invoiceTo.documentType ? invoiceTo.documentType : 'CPF',
      address: invoiceTo.address,
      stateRegistration: invoiceTo?.stateRegistration,
      email: invoiceTo.email,
      phone: invoiceTo.phone,
      [sourceInvoiceToId]: invoiceTo.id,
    }
  }

  getShipping() {
    const shipping = this.store.selectSnapshot(QuoteMetaState.getShipping)

    if (
      !shipping ||
      shipping?.shippingCost === null ||
      shipping?.shippingCost === undefined
    )
      return {
        freightType: FreightType.CIF,
        shippingCost: this.utils.calculateShippingCost(FreightType.CIF),
      }

    return {
      freightType: shipping.freightType,
      shippingCost: parseFloat(shipping.shippingCost.toFixed(2)),
    }
  }

  getInstallationValues({
    generateInstallationValue,
    kitType,
  }: {
    generateInstallationValue: boolean
    kitType: KitType
  }) {
    if (generateInstallationValue) {
      return this.store.selectSnapshot(QuoteMetaState.getInstallationValues)
    }

    const user = this.store.selectSnapshot(UserMetaState.getUser)
    const isDealer = user.roles[0] === RolesType.DEALER
    const totalPotency = this.store.selectSnapshot(
      QuoteMetaState.getTotalPotency,
    )
    if (isDealer && kitType !== KitType.CHARGER) {
      const installationPrice = totalPotency * user.installationPricePerWp
      const equipmentPrice = this.store.selectSnapshot(
        QuoteMetaState.getEquipmentPrice,
      )
      const percentage = (installationPrice * 100) / equipmentPrice
      const installationValues = {
        percentage: percentage,
        price: installationPrice,
      }
      this.store.dispatch(new SetInstallationValuesAction(installationValues))
      return installationValues
    }
    return null
  }

  getTitle(kitType: KitType): string {
    const totalPotency = this.store.selectSnapshot(
      QuoteMetaState.getTotalPotency,
    )
    const totalPotencyString = (totalPotency / 1000).toLocaleString('pt-BR')
    const titleMatrix = {
      [KitType.CUSTOM]: `Custom Kit - ${totalPotencyString} kWp`,
      [KitType.STANDARD]: `Standard Kit - ${totalPotencyString} kWp`,
      [KitType.CHARGER]: `EV Charger`,
    }
    return titleMatrix[kitType]
  }

  resetCart() {
    const cartItems = this.store.selectSnapshot(CartMetaState.getItems)
    this.store.dispatch(new ResetCartStateAction())
    cartItems.forEach((item) => {
      this.store.dispatch(
        new UpdateQuoteItemAction({
          item: this.typesMatrix[item.type] || 'panels',
          data: item,
        }),
      )
    })
  }

  clearProposalDrafts() {
    this.storage.setItem('proposalDraft', [])
  }

  checkIfCustomerInvoiceIsEmpty(): boolean {
    const invoiceTo = this.store.selectSnapshot(QuoteMetaState.getInvoiceTo)
    return !invoiceTo
  }

  verifyIfDeliverDataIsEmpty(client: any): boolean {
    if (!client.address) {
      return true
    }
    const requiredAddressFields = [
      'street',
      'number',
      'neighborhood',
      'city',
      'state',
      'zipCode',
    ]
    const missingAddressFields = requiredAddressFields.filter(
      (field) => !client.address[field] || client.address[field] === '',
    )
    return missingAddressFields.length > 0
  }

  checkIfFreightIsEmpty(): boolean {
    const freight = this.store.selectSnapshot(QuoteMetaState.getShipping)
    return !freight
  }

  verifyIfClientDataIsEmpty(client: any): boolean {
    const requiredPersonalFields = [
      /*       'firstName',
      'lastName', */
      'email',
      'phone',
      'document',
    ]
    const missingPersonalFields = requiredPersonalFields.filter(
      (field) => !client[field] || client[field] === '',
    )
    return missingPersonalFields.length > 0
  }

  patchProposalItems(kits: any[], products: any[]): void {
    const typesMatrix = {
      PANEL: 'panels',
      INVERTER: 'inverters',
      BRACKET: 'brackets',
      PROTECTION: 'protections',
      CONNECTOR: 'connectors',
      CABLE: 'cables',
      ACCESSORY: 'accessories',
    }
    if (!kits[0]) {
      const groupedItems = {}
      products.forEach((item) => {
        if (!groupedItems[item.type]) {
          groupedItems[item.type] = []
        }
        groupedItems[item.type].push(item)
      })
      Object.keys(groupedItems).forEach((key) => {
        this.store.dispatch(
          new SetQuoteDataItemAction({
            item: typesMatrix[key],
            data: groupedItems[key],
          }),
        )
      })
    } else {
      this.store.dispatch(
        new SetQuoteDataItemAction({
          item: 'panels',
          data: kits,
        }),
      )
    }
  }

  getItemsWithQuantity(proposal: any, type: string): any[] {
    if (!proposal.orderKitContents[0]['productAmounts']) {
      return []
    }
    if (type === 'kits') {
      return proposal.orderKitContents.map((item: any) => {
        const amount = item.totalPrice
        return {
          ...item,
          title: item.kitName,
          id: item.kitId,
          quantity: item.quantity,
          type: 'PANEL',
          amount,
          price: item.totalPrice / item.quantity,
        }
      })
    }
    return proposal.orderKitContents[0][type].map((item: any) => {
      const found = proposal.orderKitContents[0]['productAmounts'].find(
        (i: any) => i['productId'] === item.id,
      )
      const amount = found.quantity * found.price
      return { ...item, quantity: found.quantity, amount, price: found.price }
    })
  }

  parseProductsFromApi(data: any[]): any[] {
    return data.map((item) => {
      const parsedValue = this.utils.parseValueToBRLCurrency(item?.price)
      const image = item?.images?.length > 0 ? item?.images[0] : ''
      return {
        ...item,
        id: item?.id,
        code: item?.sapCode,
        equipment: item?.title,
        potency: item?.potency,
        availability: item?.quantityInStock > 0 ? 'Available' : 'Unavailable',
        unitType: item?.unit,
        value: parsedValue,
        origin: item?.origin,
        type: item?.type,
        image,
      }
    })
  }

  editOrder({ orderId }: { orderId: string }) {
    const loader = this.dialog.open(LoaderModalComponent, {
      width: '500px',
      height: '300px',
      panelClass: 'main-modal',
      disableClose: true,
      data: 'Aguarde enquanto carregando algumas informações...',
    })
    const data = this.parseQuotationData({
      parseQuotationType: ParseQuotationType.EDIT,
    })
    this.orderService.edit(orderId, data).subscribe({
      next: (response: any) => {
        this.store.dispatch(new SetProposalDataAction(response))
        this.resetCart()
        this.clearProposalDrafts()
        loader.close()
        this.utils
          .showSuccessMessage('Sucesso', 'Ordem editada com sucesso.')
          .then(() => {
            this.utils.navigateTo('/proposal/checkout/' + response.orderNumber)
          })
      },
      error: (error) => {
        loader.close()
        const errorMessage = this.utils.getErrorMessage(error)

        if (errorMessage === 'Shipping cost invalid to fright type CIF') {
          this.refreshShippingCost(data.shipping.freightType as FreightType)
          return
        }

        if (errorMessage === 'Installation price invalid') {
          this.refreshInstallationPrice(data.installationValues.percentage)
          return
        }

        this.utils.showErrorMessage(
          'Erro',
          errorMessage || 'Ocorreu um erro ao criar a ordem.',
        )
      },
    })
  }

  goToPreCheckout(proposalId: string) {
    const redirections = {
      [KitType.CUSTOM]: `/proposal/customization/${proposalId}`,
      [KitType.STANDARD]: `/proposal/standard/${proposalId}`,
      [KitType.CHARGER]: `/proposal/charger/${proposalId}`,
    }
    const kitType = this.utils.getProposalDataKitType()
    const proposalData = this.store.selectSnapshot(
      QuoteMetaState.getProposalData,
    )
    this.patchCheckoutItemsWithProposalData({ proposalData })
    this.utils.navigateTo(redirections[kitType])
  }

  patchAllProposalDataOnStore({ proposalData }: { proposalData: any }) {
    proposalData.poNumber = proposalData.orderNumber
    const invoiceTo = {
      ...proposalData.invoiceTo,
      displayName: proposalData.invoiceTo?.fullName,
    }
    const deliverTo = {
      ...proposalData.deliverTo,
      displayName: proposalData.deliverTo?.fullName,
    }
    this.store.dispatch(new SetProposalDataAction(proposalData))
    this.store.dispatch(new SetPaymentMethodAction(proposalData.paymentMethod))
    if (proposalData.invoice) {
      this.store.dispatch(
        new SetPaymentOriginAction(proposalData.invoice.paymentOrigin),
      )
    }
    this.store.dispatch(new SetShippingAction(proposalData.shipping))
    this.store.dispatch(
      new SetInstallationValuesAction(proposalData.installationValues),
    )
    this.store.dispatch(
      new SetCommissionValuesAction(proposalData.commissionValues),
    )
    this.store.dispatch(new SetInvoiceToAction(invoiceTo))
    this.store.dispatch(new SetDeliveryToAction(deliverTo))
    this.store.dispatch(new SetDiscountCouponAction(proposalData?.coupon))
    this.store.dispatch(
      new SetDiscountAction({
        discount: proposalData?.coupon?.value,
        discountType: proposalData?.coupon?.type,
      }),
    )
    this.store.dispatch(new SetRebateValueAction(proposalData.rebate))
    this.store.dispatch(new SetUploadedFilesAction(proposalData?.files || []))
    this.store.dispatch(
      new SetTotvsCodeAction(proposalData.orderKitContents[0].totvsCode),
    )
    this.store.dispatch(
      new SetOrderInstallationStatusAction(proposalData.installationStatus),
    )
    this.store.dispatch(new SetExternalUserAction(proposalData.externalUser))
    this.store.dispatch(new SetIntegratorAction(proposalData.integrator))
    const kitType = this.utils.getKitType({
      isKit: proposalData.orderKitContents[0].isKit,
      kitType: proposalData.orderKitContents[0].kitType,
    })
    this.store.dispatch(new SetCheckoutKitTypeAction(kitType))
    if (proposalData.tabFilters) {
      const tabFilters = this.patchTabFiltersFromProposalData(
        proposalData.tabFilters,
      )

      for (const tabFilter of tabFilters) {
        this.store.dispatch(new SetTabFilterAction(tabFilter))
      }
    }
  }

  patchTabFiltersFromProposalData(tabFilter: any) {
    const keys = Object.keys(tabFilter)
    const tabFilters: TabFilters[] = keys.reduce((acc, curr) => {
      const tab = curr
      const filters = tabFilter[curr]
      acc[curr] = { tab, filters }
      return acc
    }, [])

    return Object.values(tabFilters)
  }

  checkOrder(): {
    isValid: boolean
    action?: CheckOrderActions
    actionType?: CheckOrderActionTypes
    data?: any
  } {
    const products = this.store.selectSnapshot(
      QuoteMetaState.getSelectedKitCompositionItems,
    )
    if (products.length === 0) {
      this.utils.showWarningMessage(
        'Adicione itens ao orçamento antes de continuar.',
      )
      return { isValid: false }
    }

    const isCustomerInvoiceEmpty = this.checkIfCustomerInvoiceIsEmpty()
    if (isCustomerInvoiceEmpty) {
      this.utils.showWarningMessage('Selecione o cliente antes de continuar.')
      return { isValid: false }
    }

    const invoiceTo = this.store.selectSnapshot(QuoteMetaState.getInvoiceTo)
    const isClientDataEmpty = this.verifyIfClientDataIsEmpty(invoiceTo)
    if (isClientDataEmpty) {
      return {
        isValid: false,
        action: CheckOrderActions.MISSING_DATA,
        actionType: CheckOrderActionTypes.INVOICE_TO,
        data: invoiceTo,
      }
    }

    const deliveryTo = this.store.selectSnapshot(QuoteMetaState.getDeliveryTo)
    const isDeliveryDataEmpty = this.verifyIfDeliverDataIsEmpty(deliveryTo)
    if (isDeliveryDataEmpty) {
      return {
        isValid: false,
        action: CheckOrderActions.MISSING_DATA,
        actionType: CheckOrderActionTypes.DELIVERY_TO,
      }
    }

    const isFreightEmpty = this.checkIfFreightIsEmpty()
    if (isFreightEmpty) {
      this.utils.showWarningMessage(
        'Preencha os dados de frete antes de continuar.',
      )
      return { isValid: false }
    }

    const isExternalUserEmpty = this.checkIfExternalUserEmpty()
    if (isExternalUserEmpty) {
      this.utils.showWarningMessage(
        'It is not possible to continue as the proposal does not have a linked external user.',
      )
      return { isValid: false }
    }
    return { isValid: true }
  }

  checkIfExternalUserEmpty(): boolean {
    const externalUser = this.store.selectSnapshot(
      QuoteMetaState.getExternalUser,
    )

    return !externalUser
  }

  patchCheckoutItemsWithProposalData({ proposalData }: { proposalData: any }) {
    const orderKitContentData = []
    const kitType = this.utils.getKitType({
      isKit: proposalData.orderKitContents[0].isKit,
      kitType: proposalData.orderKitContents[0].kitType,
    })

    if (!kitType || kitType === KitType.CUSTOM) return

    for (const kitContent of proposalData.orderKitContents) {
      if (kitType === KitType.STANDARD) {
        const item = kitContent.kit
        item.quantity = kitContent.quantity
        orderKitContentData.push(item)
        continue
      }

      for (const productAmount of kitContent.productAmounts) {
        const product = kitContent.products.find(
          (item: any) => item.id === productAmount.productId,
        )

        if (!product) continue

        product.quantity = productAmount.quantity

        orderKitContentData.push(product)
      }
    }

    if (kitType === KitType.STANDARD) {
      const parsedItems = this.parseStandardCartItems(
        orderKitContentData.flat(),
      )
      this.store.dispatch(new SetStandardKitCompositionAction(parsedItems))
      return
    }

    const parsedItems = this.parseCustomAndChargerCartItems(
      orderKitContentData.flat(),
    )
    this.store.dispatch(
      new SetChargerKitCompositionAction(parsedItems as StoreChargers[]),
    )
  }

  parseCustomAndChargerCartItems(
    cartItems: Product[],
  ): IQuoteItem[] | StoreChargers[] {
    return cartItems.map((item) => {
      const weight = item?.weight ? item?.weight.replace(',', '.') : '0'
      return {
        id: item?.id,
        code: item?.sapCode,
        equipment: item?.title,
        availability: item?.quantityInStock > 0 ? 'Available' : 'Unavailable',
        quantity: item?.quantity,
        value: item?.value,
        price: item?.value,
        amount: this.utils.parseValueToBRLCurrency(
          item?.quantity * item?.value,
        ),
        potency: item.potency,
        type: item?.type,
        weight: isNaN(+weight) ? 0 : +weight,
      }
    })
  }

  parseStandardCartItems(cartItems: StoreKit[]): StoreKit[] {
    return cartItems.map((item) => {
      return {
        ...item,
        amount: this.utils.parseValueToBRLCurrency(item?.quantity * item.value),
      }
    })
  }

  translateErrors(message: string) {
    const errorKeys = [
      'The following products are unavailable:',
      'Product not found with ID',
      'Insufficient stock for product',
      'Inconsistency error. User is not an external user',
      'Cannot change payment method or convert Proposal to Purchase Order because the proposal is expired',
    ]

    for (const key of errorKeys) {
      if (message.includes(key)) {
        if (message.length === key.length) {
          return this.utils.getTranslatedKey(message)
        }
        const errorDetails = message.split(key)[1]?.trim()
        return this.utils.getTranslatedKey(key) + ' ' + errorDetails
      }
    }

    return message
  }

  calculateInstallationValue(percentage: number): void {
    const equipmentPrice = this.store.selectSnapshot(
      QuoteMetaState.getEquipmentPrice,
    )
    const installationPrice = (equipmentPrice * percentage) / 100
    this.store.dispatch(
      new SetInstallationValuesAction({
        percentage,
        price: +installationPrice.toFixed(2),
      }),
    )
  }

  private refreshShippingCost(freightType: FreightType) {
    this.utils.showErrorMessage(
      'Error',
      'Unable to proceed, delivery outdated. Try again.',
    )
    const shippingCost = this.utils.calculateShippingCost(freightType)
    this.store.dispatch(
      new SetShippingAction({
        freightType,
        shippingCost,
      }),
    )
  }

  private refreshInstallationPrice(percentage: number) {
    this.utils.showErrorMessage(
      'Error',
      'Unable to proceed, installation price outdated. Try again.',
    )
    const equipmentPrice = this.store.selectSnapshot(
      QuoteMetaState.getEquipmentPrice,
    )
    const installationPrice = (equipmentPrice * percentage) / 100
    this.store.dispatch(
      new SetInstallationValuesAction({
        percentage,
        price: +installationPrice.toFixed(2),
      }),
    )
  }
}
